import * as React from 'react';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { Formik, Field, Form, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

// COMPONENTS
import SingleSelect from '../../../components/SingleSelect';
import FileUpload from '../../../components/FileUpload';
import AddLaterRadio from '../../../components/AddLaterRadio';
import Button from '../../../components/Button';
import ExistingCertification from '../../../components/ExistingCertification';
import DateInput from '../../../components/DateInput';

// TYPES
import { CertificationTypes } from './CertificationStaff';
import { FileType } from '../../../components/FileUpload';
import { AccountExistingCertificationsCertification, DomainCertificationType } from '../../../api/models';

// ICONS
import { ReactComponent as PlusIcon } from '../../../assets/svg/plus.svg';
import { ReactComponent as MinusIcon } from '../../../assets/svg/minus.svg';

interface PartFourTypes {
	certificationData: CertificationTypes;
	setFormSection: React.Dispatch<React.SetStateAction<string>>;
	setCertificationFormData: React.Dispatch<React.SetStateAction<CertificationTypes>>;
	existingCertifications: AccountExistingCertificationsCertification[];
}

interface QualificationCertificateTypes {
	qualificationCertificateType?: DomainCertificationType;
	title?: string;
	date?: string;
	organisation?: string;
	createAttachments?: string[];
	attachments?: FileType[];
}

const PartFour: React.FC<PartFourTypes> = ({
	certificationData,
	setFormSection,
	setCertificationFormData,
	existingCertifications
}) => {
	// Set initial certification template
	const certificationTemplate: QualificationCertificateTypes = {
		qualificationCertificateType: undefined,
		title: '',
		date: '',
		organisation: '',
		attachments: [],
		createAttachments: []
	};

	//Set Initial Values
	const partFourInitialValues = {
		qualificationCertifications: certificationData.qualificationCertificate
			? [...certificationData.qualificationCertificate]
			: [{ ...certificationTemplate }],

		addLater: existingCertifications?.some((item) => item.validCertification)
	};

	const qualificationCertificationsList = useAppSelector((state) => state?.lists.qualificationCertifications);

	//Submit form
	function continueFormStepThree(values: typeof partFourInitialValues) {
		setFormSection('5');
		if (!values.addLater) {
			setCertificationFormData((prevState) => ({
				...prevState,
				qualificationCertificate: values.qualificationCertifications.map(
					(qualification: QualificationCertificateTypes) => ({
						qualificationCertificateType: qualification.qualificationCertificateType,
						title: qualification.title,
						date: qualification.date,
						organisation: qualification.organisation,
						attachments: qualification.attachments,
						createAttachments: qualification.createAttachments
					})
				)
			}));
		} else {
			setCertificationFormData((prevState) => ({
				...prevState,
				qualificationCertificate: [{ ...certificationTemplate }]
			}));
		}
	}

	//Validation
	const formThreeValidationSchema = Yup.object().shape({
		qualificationCertifications: Yup.array().when('addLater', {
			is: false,
			then: Yup.array().of(
				Yup.object({
					qualificationCertificateType: Yup.string().required('Required'),
					title: Yup.string().required('Required'),
					date: Yup.string().required('Required'),
					organisation: Yup.string().required('Required'),
					createAttachments: Yup.array().min(1, 'Required').required()
				})
			)
		})
	});

	return (
		<Formik
			validationSchema={formThreeValidationSchema}
			onSubmit={continueFormStepThree}
			initialValues={partFourInitialValues}
			enableReinitialize={true}
			validateOnMount={true}
		>
			{(formik) => {
				return (
					<Form translate="yes">
						<div className="form-group">
							<label htmlFor="qualificationCertifications" className="label">
								4. Please provide evidence of qualifications
							</label>

							<FieldArray name="qualificationCertifications">
								{(props) => {
									const { push, remove, form } = props;
									const { values } = form;
									const { qualificationCertifications } = values;

									return (
										<>
											{qualificationCertifications.map((certificate: any, index: number) => {
												return (
													<div key={`qualification-${index}`}>
														<div className="form-group">
															{!formik.values.addLater && index !== 0 ? (
																<>
																	<div className="addition-container">
																		<label htmlFor="accountCertification" className="label">
																			Additional credentials
																		</label>

																		<button
																			onClick={() => remove(index)}
																			type="button"
																			className="remove-credentials full-width"
																		>
																			<MinusIcon className="icon" />
																			Remove
																		</button>
																	</div>

																	<Field
																		children={() => {
																			return (
																				<SingleSelect
																					name={`qualificationCertifications[${index}].qualificationCertificateType`}
																					onChange={(val: string) => {
																						formik.setFieldValue(
																							`qualificationCertifications[${index}].qualificationCertificateType`,
																							val
																						);
																						formik.setFieldValue(`addLater`, false);
																					}}
																					options={qualificationCertificationsList}
																					value={
																						formik.values.addLater
																							? ''
																							: formik.values.qualificationCertifications[index]
																									.qualificationCertificateType
																					}
																					placeholder="Select level of qualification..."
																				/>
																			);
																		}}
																	/>
																</>
															) : index === 0 ? (
																<Field
																	children={() => {
																		return (
																			<SingleSelect
																				name={`qualificationCertifications[${index}].qualificationCertificateType`}
																				onChange={(val: string) => {
																					formik.setFieldValue(
																						`qualificationCertifications[${index}].qualificationCertificateType`,
																						val
																					);
																					formik.setFieldValue(`addLater`, false);
																				}}
																				options={qualificationCertificationsList}
																				value={
																					formik.values.addLater
																						? ''
																						: formik.values.qualificationCertifications[index]
																								.qualificationCertificateType
																				}
																				placeholder="Select level of qualification..."
																			/>
																		);
																	}}
																/>
															) : null}

															<ErrorMessage name={`qualificationCertifications[${index}].qualificationCertificateType`}>
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														{!formik.values.addLater &&
														formik.values.qualificationCertifications[index].qualificationCertificateType ? (
															<>
																<div className="form-group">
																	<label htmlFor={`qualificationCertifications[${index}].title`}>
																		Title of qualification
																	</label>

																	<Field
																		name={`qualificationCertifications[${index}].title`}
																		className="form-control"
																		type="text"
																		placeholder=""
																		onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																			formik.setFieldValue(
																				`qualificationCertifications[${index}].title`,
																				e.target.value
																			);
																		}}
																		value={formik.values.qualificationCertifications[index].title}
																	/>

																	<ErrorMessage name={`qualificationCertifications[${index}].title`}>
																		{(error) => <div className="form-validation-error">{error}</div>}
																	</ErrorMessage>
																</div>

																<div className="form-group">
																	<label htmlFor={`qualificationCertifications[${index}].date`}>Date</label>

																	<DateInput
																		showDropdowns={true}
																		excludeFuture={true}
																		dateTimeString={formik.values.qualificationCertifications[index].date || ''}
																		onChange={(val: string) => {
																			formik.setFieldValue(`qualificationCertifications[${index}].date`, val);
																		}}
																	/>

																	<ErrorMessage name={`qualificationCertifications[${index}].date`}>
																		{(error) => <div className="form-validation-error">{error}</div>}
																	</ErrorMessage>
																</div>

																<div className="form-group">
																	<label htmlFor={`qualificationCertifications[${index}].organisation`}>
																		Organisation obtained from
																	</label>

																	<Field
																		name={`qualificationCertifications[${index}].organisation`}
																		className="form-control"
																		type="text"
																		placeholder=""
																		onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
																			formik.setFieldValue(
																				`qualificationCertifications[${index}].organisation`,
																				e.target.value
																			);
																		}}
																		value={formik.values.qualificationCertifications[index].organisation}
																	/>

																	<ErrorMessage name={`qualificationCertifications[${index}].organisation`}>
																		{(error) => <div className="form-validation-error">{error}</div>}
																	</ErrorMessage>
																</div>

																<div className="form-group">
																	<Field
																		children={() => (
																			<>
																				<FileUpload
																					label="Attachments"
																					name={`qualificationCertifications[${index}].createAttachments`}
																					placeholder="Choose file..."
																					multiple={true}
																					onChange={(val: string[], files: any[]) => {
																						formik.setFieldValue(
																							`qualificationCertifications[${index}].createAttachments`,
																							val
																						);
																					}}
																					files={formik.values.qualificationCertifications[index].attachments}
																				/>
																			</>
																		)}
																	/>{' '}
																	<ErrorMessage name={`qualificationCertifications[${index}].createAttachments`}>
																		{(error) => <div className="form-validation-error">{error}</div>}
																	</ErrorMessage>
																</div>

																{formik.isValid &&
																formik.values.qualificationCertifications[index].qualificationCertificateType &&
																formik.values.qualificationCertifications.length - 1 === index ? (
																	<button
																		onClick={() => push({ ...certificationTemplate })}
																		type="button"
																		className="add-more-credentials full-width"
																	>
																		<PlusIcon className="icon" />
																		Add another credential
																	</button>
																) : null}
															</>
														) : null}
													</div>
												);
											})}
										</>
									);
								}}
							</FieldArray>

							<AddLaterRadio
								changeToUseExisting={existingCertifications && existingCertifications.length > 0}
								onChange={(val: boolean) => {
									formik.setFieldValue('addLater', val);
									// Clear all entered qualifications (including extra entries) so leftover input is not displayed when switching back to manual entry
									if (val) {
										formik.setFieldValue('qualificationCertifications', [{ ...certificationTemplate }]);
										// Also reset touched state so the cleared fields do not immediately show validation errors when re-entering details
										formik.setFieldTouched('qualificationCertifications', false);
									}
								}}
								value={formik.values.addLater}
							/>
						</div>

						<div className="general-divider"></div>

						{existingCertifications && existingCertifications.length > 0 ? (
							<ExistingCertification existingCertifications={existingCertifications} />
						) : null}

						<Button type="submit" className="btn btn-primary full-width">
							Continue
						</Button>
					</Form>
				);
			}}
		</Formik>
	);
};

export default PartFour;
